import { request } from "./http";

const SUBSCRIPTION_STATE_PATHS = [
  "/api/me/subscription",
  "/api/me/subscriptions/current",
  "/api/me/psychologist-subscription",
  "/api/subscriptions/me",
  "/api/me/billing/subscription",
];

const SUBSCRIPTION_PLANS_PATHS = [
  "/api/subscriptions/plans",
  "/api/me/subscriptions/plans",
  "/api/tariffs/plans",
  "/api/public/subscriptions/plans",
];

const SUBSCRIPTION_CHECKOUT_PATHS = [
  "/api/subscriptions/checkout",
  "/api/me/subscriptions/checkout",
  "/api/subscriptions/yookassa/create",
  "/api/billing/subscriptions/checkout",
];

const SUBSCRIPTION_PREVIEW_PATHS = [
  "/api/subscriptions/preview",
  "/api/me/subscriptions/preview",
  "/api/billing/subscriptions/preview",
  "/api/subscriptions/checkout/preview",
];

const SUBSCRIPTION_CANCEL_PATHS = [
  "/api/me/subscription/cancel",
  "/api/subscriptions/cancel",
  "/api/me/subscriptions/cancel",
];

const BILLING_MODE_MONTH = "month";
const BILLING_MODE_YEAR = "year";

function canFallback(err) {
  const status = err?.status ?? err?.response?.status ?? null;
  return status === 404 || status === 405 || status === 501;
}

async function requestFirstAvailable(paths = [], options = {}) {
  let lastErr = null;
  for (const path of paths) {
    try {
      return await request(path, options);
    } catch (err) {
      if (!canFallback(err)) throw err;
      lastErr = err;
    }
  }
  if (lastErr) throw lastErr;
  throw new Error("No endpoint paths configured");
}

function unwrapPayload(raw) {
  if (raw && typeof raw === "object") {
    if (raw.item && typeof raw.item === "object") return raw.item;
    if (raw.data && typeof raw.data === "object") return raw.data;
    if (raw.subscription && typeof raw.subscription === "object") return raw.subscription;
    if (raw.payment && typeof raw.payment === "object") return raw.payment;
  }
  return raw;
}

function normalizeBillingMode(raw) {
  const token = String(raw || "").trim().toLowerCase();
  if (["year", "yearly", "annual", "annually", "12m", "year_12"].includes(token)) return BILLING_MODE_YEAR;
  return BILLING_MODE_MONTH;
}

function normalizeStatus(raw) {
  const token = String(raw || "").trim().toUpperCase();
  if (!token) return "UNKNOWN";
  return token;
}

function toInt(value, fallback = 0) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.round(num);
}

function parseDate(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function daysLeftUntil(dateIso) {
  if (!dateIso) return 0;
  const target = new Date(dateIso).getTime();
  if (!Number.isFinite(target)) return 0;
  const diff = target - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

function sanitizePlanCode(raw) {
  const code = String(raw || "").trim().toUpperCase();
  if (code) return code;
  return "FREE";
}

const DEFAULT_PLANS = [
  {
    code: "FREE",
    name: "Бесплатный",
    description: "Чтобы попробовать без риска",
    monthlyPrice: 0,
    yearlyTotal: 0,
    isPopular: false,
    features: [
      { label: "До 2 клиентов", enabled: true },
      { label: "До 10 сессий в месяц", enabled: true },
      { label: "Основной функционал платформы", enabled: true },
      { label: "Профиль виден новым клиентам", enabled: false },
      { label: "Расширенная аналитика", enabled: false },
    ],
  },
  {
    code: "BASIC",
    name: "Базовый",
    description: "Для активной практики",
    monthlyPrice: 890,
    yearlyTotal: 9590,
    isPopular: true,
    features: [
      { label: "До 7 клиентов", enabled: true },
      { label: "Сессии без ограничений", enabled: true },
      { label: "Профиль виден новым клиентам", enabled: true },
      { label: "Финансовые отчеты", enabled: true },
      { label: "Расширенная аналитика", enabled: false },
    ],
  },
  {
    code: "PRO",
    name: "Расширенный",
    description: "Для тех, кто хочет максимум",
    monthlyPrice: 1490,
    yearlyTotal: 15990,
    isPopular: false,
    features: [
      { label: "Клиенты без ограничений", enabled: true },
      { label: "Сессии без ограничений", enabled: true },
      { label: "Расширенная аналитика", enabled: true },
      { label: "Ранний доступ к функциям", enabled: true },
      { label: "Приоритетная поддержка", enabled: true },
    ],
  },
];

function normalizePlan(raw = {}, fallback = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const base = fallback && typeof fallback === "object" ? fallback : {};

  const monthlyPrice = toInt(
    source.monthlyPrice ?? source.monthPrice ?? source.priceMonth ?? source.priceMonthly ?? base.monthlyPrice,
    0
  );

  const yearlyTotal = toInt(
    source.yearlyPrice ?? source.yearlyTotal ?? source.annualPrice ?? source.priceYear ?? base.yearlyTotal,
    monthlyPrice > 0 ? monthlyPrice * 12 : 0
  );

  const rawFeatures = Array.isArray(source.features)
    ? source.features
    : Array.isArray(source.capabilities)
      ? source.capabilities
      : Array.isArray(base.features)
        ? base.features
        : [];

  const features = rawFeatures
    .map((feature) => {
      if (typeof feature === "string") {
        return { label: feature, enabled: true };
      }
      if (!feature || typeof feature !== "object") return null;
      const label = String(feature.label || feature.title || feature.name || "").trim();
      if (!label) return null;
      const enabled = feature.enabled !== false && !String(feature.status || "").toUpperCase().includes("LOCK");
      return { label, enabled };
    })
    .filter(Boolean);

  return {
    code: sanitizePlanCode(source.code || source.planCode || source.id || source.key || base.code),
    name: String(source.name || source.title || source.planName || base.name || "Тариф").trim() || "Тариф",
    description: String(source.description || source.subtitle || source.tagline || base.description || "").trim(),
    monthlyPrice,
    yearlyTotal,
    isPopular: Boolean(source.isPopular ?? source.popular ?? base.isPopular),
    features,
  };
}

function normalizePlans(raw) {
  const source = unwrapPayload(raw);
  const items = Array.isArray(source)
    ? source
    : Array.isArray(source?.items)
      ? source.items
      : Array.isArray(source?.content)
        ? source.content
        : [];

  if (!items.length) {
    return DEFAULT_PLANS.map((plan) => normalizePlan(plan));
  }

  const byCode = new Map();
  for (const fallbackPlan of DEFAULT_PLANS) {
    byCode.set(fallbackPlan.code, normalizePlan(fallbackPlan));
  }

  for (const item of items) {
    const code = sanitizePlanCode(item?.code || item?.planCode || item?.id || item?.key);
    const fallback = byCode.get(code) || {};
    byCode.set(code, normalizePlan(item, fallback));
  }

  return [...byCode.values()];
}

function resolvePlanName(code, plans = DEFAULT_PLANS) {
  const token = sanitizePlanCode(code);
  const matched = plans.find((plan) => sanitizePlanCode(plan.code) === token);
  if (matched?.name) return matched.name;
  if (token === "FREE") return "Бесплатный";
  if (token === "BASIC") return "Базовый";
  if (token === "PRO") return "Расширенный";
  return token;
}

function normalizeSubscriptionState(raw = {}, plans = DEFAULT_PLANS) {
  const source = unwrapPayload(raw) || {};
  const planCode = sanitizePlanCode(
    source.planCode ?? source.plan ?? source.currentPlanCode ?? source.tariffCode ?? source.subscriptionPlan ?? "FREE"
  );

  const trialEndsAt = parseDate(
    source.trialEndsAt ?? source.trialEndAt ?? source.trialUntil ?? source.trialEndDate ?? source.freeTrialEndsAt
  );

  const nextBillingDate = parseDate(
    source.nextBillingDate ?? source.nextChargeAt ?? source.nextPaymentDate ?? source.nextChargeDate
  );

  const billingMode = normalizeBillingMode(
    source.billingMode ?? source.period ?? source.billingPeriod ?? source.interval
  );

  const status = normalizeStatus(source.status ?? source.subscriptionStatus);
  const trialActiveRaw = source.trialActive ?? source.isTrial ?? source.onTrial;
  const trialActive =
    typeof trialActiveRaw === "boolean"
      ? trialActiveRaw
      : status.includes("TRIAL") || daysLeftUntil(trialEndsAt) > 0;

  const canCancelRaw = source.canCancel ?? source.cancellable ?? source.cancelAllowed;
  const canCancel = typeof canCancelRaw === "boolean" ? canCancelRaw : planCode !== "FREE";

  const amountMonthly = toInt(
    source.monthlyPrice ?? source.priceMonthly ?? source.currentMonthlyPrice,
    planCode === "FREE" ? 0 : 890
  );

  const amountYearly = toInt(
    source.yearlyPrice ?? source.currentYearlyPrice,
    amountMonthly > 0 ? amountMonthly * 12 : 0
  );

  return {
    planCode,
    planName:
      String(source.planName || source.currentPlanName || "").trim() ||
      resolvePlanName(planCode, plans),
    status,
    billingMode,
    trialActive,
    trialEndsAt,
    trialDaysLeft: daysLeftUntil(trialEndsAt),
    nextBillingDate,
    canCancel,
    amountMonthly,
    amountYearly,
    raw: source,
  };
}

function normalizeCheckoutResponse(raw = {}) {
  const source = unwrapPayload(raw) || {};

  const confirmationUrl =
    source.confirmationUrl ??
    source.confirmUrl ??
    source.url ??
    source.paymentUrl ??
    source.redirectUrl ??
    source.confirmation?.confirmation_url ??
    source.confirmation?.confirmationUrl ??
    source.payment?.confirmation?.confirmation_url ??
    source.payment?.confirmation?.confirmationUrl ??
    "";

  return {
    paymentId: String(source.paymentId ?? source.id ?? source.payment?.id ?? "").trim(),
    confirmationUrl: String(confirmationUrl || "").trim(),
    status: normalizeStatus(source.status ?? source.payment?.status),
    raw: source,
  };
}

function normalizeCheckoutPreviewResponse(raw = {}) {
  const source = unwrapPayload(raw) || {};
  return {
    discountApplied: Boolean(source.discountApplied),
    discountCode: String(source.discountCode || source.promoCode || source.certificateCode || "").trim(),
    discountCodeType: String(source.discountCodeType || "").trim(),
    discountValueType: String(source.discountValueType || "").trim(),
    discountValueAmount: Number(source.discountValueAmount) || 0,
    discountAmount: Number(source.discountAmount) || 0,
    baseAmount: Number(source.baseAmount ?? source.originalAmount) || 0,
    finalAmount: Number(source.finalAmount ?? source.amount ?? source.baseAmount) || 0,
    currency: String(source.currency || "RUB").trim() || "RUB",
    raw: source,
  };
}

function buildCheckoutPayload({
  planCode,
  billingMode,
  paymentMethod,
  returnUrl,
  cancelUrl,
  discountCode,
  promoCode,
  certificateCode,
} = {}) {
  const cleanPlanCode = sanitizePlanCode(planCode);
  const cleanMode = normalizeBillingMode(billingMode);
  const method = String(paymentMethod || "card").toLowerCase();
  const normalizedPromoCode = String(promoCode || "").trim();
  const normalizedCertificateCode = String(certificateCode || "").trim();
  const normalizedDiscountCode = String(discountCode || normalizedPromoCode || normalizedCertificateCode || "").trim();

  const discountFields = {};
  if (normalizedDiscountCode) discountFields.discountCode = normalizedDiscountCode;
  if (normalizedPromoCode) discountFields.promoCode = normalizedPromoCode;
  if (normalizedCertificateCode) discountFields.certificateCode = normalizedCertificateCode;

  return {
    planCode: cleanPlanCode,
    billingMode: cleanMode,
    paymentMethod: method,
    returnUrl: String(returnUrl || "").trim(),
    cancelUrl: String(cancelUrl || "").trim(),
    ...discountFields,
    yookassa: {
      paymentMethod: method,
      returnUrl: String(returnUrl || "").trim(),
      ...discountFields,
    },
  };
}

function buildCheckoutCandidates(payload) {
  const compactDiscount =
    String(payload.discountCode || payload.promoCode || payload.certificateCode || "").trim() || undefined;

  return [
    payload,
    {
      planCode: payload.planCode,
      period: payload.billingMode,
      paymentMethod: payload.paymentMethod,
      returnUrl: payload.returnUrl,
      cancelUrl: payload.cancelUrl,
      ...(compactDiscount ? { discountCode: compactDiscount, code: compactDiscount } : {}),
      ...(payload.promoCode ? { promoCode: payload.promoCode } : {}),
      ...(payload.certificateCode ? { certificateCode: payload.certificateCode } : {}),
    },
    {
      tariff: payload.planCode,
      billingPeriod: payload.billingMode,
      method: payload.paymentMethod,
      successUrl: payload.returnUrl,
      failUrl: payload.cancelUrl,
      ...(compactDiscount ? { coupon: compactDiscount, couponCode: compactDiscount } : {}),
      ...(payload.promoCode ? { promoCode: payload.promoCode } : {}),
      ...(payload.certificateCode ? { certificateCode: payload.certificateCode } : {}),
    },
  ];
}

export const subscriptionsApi = {
  async getMySubscription({ plans } = {}) {
    const normalizedPlans = Array.isArray(plans) && plans.length ? plans : DEFAULT_PLANS;
    try {
      const raw = await requestFirstAvailable(SUBSCRIPTION_STATE_PATHS, { method: "GET" });
      return normalizeSubscriptionState(raw, normalizedPlans);
    } catch (err) {
      if (!canFallback(err)) throw err;
      return normalizeSubscriptionState({}, normalizedPlans);
    }
  },

  async listPlans() {
    try {
      const raw = await requestFirstAvailable(SUBSCRIPTION_PLANS_PATHS, { method: "GET" });
      return normalizePlans(raw);
    } catch (err) {
      if (!canFallback(err)) throw err;
      return normalizePlans([]);
    }
  },

  async createCheckout({
    planCode,
    billingMode,
    paymentMethod = "card",
    returnUrl,
    cancelUrl,
    discountCode,
    promoCode,
    certificateCode,
  } = {}) {
    const payload = buildCheckoutPayload({
      planCode,
      billingMode,
      paymentMethod,
      returnUrl,
      cancelUrl,
      discountCode,
      promoCode,
      certificateCode,
    });
    const candidates = buildCheckoutCandidates(payload);

    let lastErr = null;
    for (const path of SUBSCRIPTION_CHECKOUT_PATHS) {
      for (const body of candidates) {
        try {
          const raw = await request(path, { method: "POST", json: body });
          return normalizeCheckoutResponse(raw);
        } catch (err) {
          const status = err?.status ?? err?.response?.status ?? null;
          if (canFallback(err)) {
            lastErr = err;
            break;
          }
          if (status === 400 && candidates.length > 1) {
            lastErr = err;
            continue;
          }
          throw err;
        }
      }
    }

    if (lastErr) throw lastErr;
    throw new Error("Checkout endpoint unavailable");
  },

  async previewCheckout({
    planCode,
    billingMode,
    paymentMethod = "card",
    returnUrl,
    cancelUrl,
    discountCode,
    promoCode,
    certificateCode,
  } = {}) {
    const payload = buildCheckoutPayload({
      planCode,
      billingMode,
      paymentMethod,
      returnUrl,
      cancelUrl,
      discountCode,
      promoCode,
      certificateCode,
    });
    const candidates = buildCheckoutCandidates(payload);

    let lastErr = null;
    for (const path of SUBSCRIPTION_PREVIEW_PATHS) {
      for (const body of candidates) {
        try {
          const raw = await request(path, { method: "POST", json: body });
          return normalizeCheckoutPreviewResponse(raw);
        } catch (err) {
          const status = err?.status ?? err?.response?.status ?? null;
          if (canFallback(err)) {
            lastErr = err;
            break;
          }
          if (status === 400 && candidates.length > 1) {
            lastErr = err;
            continue;
          }
          throw err;
        }
      }
    }

    if (lastErr) throw lastErr;
    throw new Error("Checkout preview endpoint unavailable");
  },

  async cancelSubscription() {
    let lastErr = null;
    for (const path of SUBSCRIPTION_CANCEL_PATHS) {
      try {
        return await request(path, { method: "POST", json: {} });
      } catch (err) {
        if (!canFallback(err)) throw err;
        lastErr = err;
      }
    }
    if (lastErr) throw lastErr;
    return { ok: true };
  },
};
